import { Box, Flex } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";

export type DonutSegment = { value: number; color: string };

/**
 * Lekki, bezzależnościowy donut/gauge na SVG.
 * - jeden segment + value<100 → wskaźnik (np. frekwencja),
 * - kilka segmentów sumujących się do 100 → rozkład (np. TAK/NIE).
 * Animacja: stroke-dashoffset rośnie od 0 po zamontowaniu (kaskadowo).
 */
export const DonutChart: React.FC<{
  segments: DonutSegment[];
  size?: number;
  thickness?: number;
  trackColor?: string;
  children?: React.ReactNode;
}> = ({ segments, size = 150, thickness = 16, trackColor = "rgba(67,0,152,0.08)", children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const cap = segments.length > 1 ? "butt" : "round";
  let acc = 0;

  return (
    <Box position="relative" w={`${size}px`} h={`${size}px`} flexShrink={0}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={thickness} />
        {segments.map((s, i) => {
          const len = (Math.min(Math.max(s.value, 0), 100) / 100) * c;
          const startAngle = -90 + (acc / 100) * 360;
          acc += s.value;
          return (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={thickness}
              strokeLinecap={cap as "butt" | "round"}
              strokeDasharray={c}
              strokeDashoffset={mounted ? c - len : c}
              style={{
                transition: `stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1) ${i * 0.12}s`,
                transform: `rotate(${startAngle}deg)`,
                transformOrigin: "50% 50%"
              }}
            />
          );
        })}
      </svg>
      {children && (
        <Flex position="absolute" inset="0" align="center" justify="center" direction="column" textAlign="center" px="6px">
          {children}
        </Flex>
      )}
    </Box>
  );
};
